import React, { useEffect, useState } from 'react';

import { useFetchData } from '../../infrastructure/hooks/useFetchData';
import { getOrderCharges } from '../../services/OrdersService';
import { Order } from '../../types/Order';
import { Party } from '../../types/Party';
import { ApiStatus } from '../../infrastructure/constants/apiStatus';
import { StripeCharge, StripeRefund } from '../../types/Stripe';
import { timestampToDateString } from '../../infrastructure/utils/common';
import { stripeChargeUrl } from '../../libs/stripeLib';
import DollarOutput from '../utils/DollarOutput';

import RequestRefundModal from './RequestRefundModal';
import StripeChargesTable from './StripeChargesTable/StripeChargesTable';
import {
  StripeChargeRowProps,
  StripeRefundRowProps,
} from './StripeChargesTable/StripeChargesRow';

type Props = {
  party: Partial<Party>;
  order: Order & { id: string };
};

const getChargeRefunds = (charge: StripeCharge) => {
  return (charge.refunds?.data || []).filter(
    refund => refund.status !== 'canceled'
  );
};

const OrderStripeCharges = ({ party, order }: Props) => {
  const [apiStatus, charges, fetchCharges, reFetchCharges] = useFetchData<
    StripeCharge[]
  >();

  const [totalChargedInCents, setTotalChargedInCents] = useState<number>(0);
  const [totalRefundInCents, setTotalRefundInCents] = useState<number>(0);

  useEffect(() => {
    fetchCharges(() => getOrderCharges(order.id));
  }, [order.id, fetchCharges]);
  useEffect(() => {
    let charged = 0,
      refund = 0;
    (charges || []).forEach(charge => {
      charged += charge.amount;
      refund += getChargeRefunds(charge).reduce(
        (sum, refund) => sum + refund.amount,
        0
      );
    });

    setTotalChargedInCents(charged);
    setTotalRefundInCents(refund);
  }, [charges]);

  const [requestRefundModalOpen, setRequestRefundModalOpen] = useState<boolean>(
    false
  );

  const closeModal = () => {
    setRequestRefundModalOpen(false);
    // Refresh the charge list after the modal closes.
    reFetchCharges();
  };

  const convertStripeRefund = (refund: StripeRefund): StripeRefundRowProps => ({
    id: refund.id,
    amount: refund.amount / 100,
    status: refund.status,
    createdAt: timestampToDateString(refund.created * 1000, party.tz),
  });
  const convertStripeCharge = (charge: StripeCharge): StripeChargeRowProps => {
    const refunds = getChargeRefunds(charge);
    return {
      id: charge.id,
      amount: charge.amount / 100,
      refundAmount:
        refunds.map(r => r.amount).reduce((total, next) => total + next, 0) /
        100,
      status: charge.status,
      createdAt: timestampToDateString(charge.created * 1000, party.tz),
      stripeLink: stripeChargeUrl(charge.id),
      refunds: refunds.map(convertStripeRefund),
    };
  };

  if (apiStatus === ApiStatus.loading)
    return <div>Loading Stripe charges...</div>;

  if (apiStatus === ApiStatus.error)
    return <div>An error occurred while loading the stripe charges.</div>;

  if (!charges || charges.length === 0)
    return <div>There are no charges associated with this order.</div>;

  return (
    <>
      <h4 className={'stripe-charges__header clearfix'}>
        Stripe Charges
        <small>
          {!!totalChargedInCents && (
            <span>
              Total Charged: <DollarOutput value={totalChargedInCents / 100} />
            </span>
          )}
          {!!totalRefundInCents && (
            <span>
              {' '}
              / Total Refund: <DollarOutput value={totalRefundInCents / 100} />
            </span>
          )}
        </small>
        <button
          className={'btn btn-danger pull-right'}
          onClick={() => {
            setRequestRefundModalOpen(true);
          }}
        >
          Request Refund
        </button>
      </h4>

      <StripeChargesTable charges={charges.map(convertStripeCharge)} />
      {requestRefundModalOpen && (
        <RequestRefundModal
          order={order}
          onClose={closeModal}
          variant='reasonChoice'
        />
      )}
    </>
  );
};

export default OrderStripeCharges;
